import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useTranslations } from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import {
  cardNotificationsService,
  type CardNotificationEvent,
  type CardNotificationPreferences,
} from "../services/cardNotifications.service";

export const CARD_NOTIFICATION_EVENTS: CardNotificationEvent[] = [
  "recharge",
  "transaction_declined",
  "transaction_reversed",
  "refund_received",
  "blocked",
  "unblocked",
  "withdrawal",
  "balance_low",
  "transaction_approved",
];

const DEFAULT_PREFS: CardNotificationPreferences = {
  recharge: false,
  transaction_approved: false,
  transaction_declined: false,
  transaction_reversed: false,
  refund_received: false,
  blocked: false,
  unblocked: false,
  withdrawal: false,
  balance_low: false,
  balance_low_threshold_usd: null,
};

const parsePositive = (raw: string): number | null => {
  const parsed = Number.parseFloat(raw.trim());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * The organization's card notification preferences, as the page consumes them.
 *
 * Turning one ON is confirmed first (`pendingToggle`), because it starts sending real messages to real
 * cardholders; turning one off is immediate. Writes are optimistic and roll back on failure.
 */
export function useCardNotificationPreferences() {
  const { t } = useTranslations("notificationPreferences");
  const { t: tCards } = useTranslations("cards");
  const { user, can } = useAuth();
  const organizationId = user?.organization_id;
  const canWrite = can("cards:write");

  const [prefs, setPrefs] = useState<CardNotificationPreferences>(DEFAULT_PREFS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<CardNotificationEvent | null>(null);
  const [thresholdInput, setThresholdInput] = useState<string>("");
  const [thresholdError, setThresholdError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    cardNotificationsService
      .get(organizationId)
      .then((next) => {
        if (cancelled) return;
        setPrefs(next);
        setThresholdInput(
          next.balance_low_threshold_usd != null ? String(next.balance_low_threshold_usd) : "",
        );
      })
      .catch((error: unknown) => {
        if (!cancelled && HttpApiService.isAccessDeniedError(error)) setHasNoAccess(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const applyPatch = useCallback(
    async (patch: Partial<CardNotificationPreferences>) => {
      if (!organizationId || isSaving) return;
      const previous = prefs;
      setPrefs((p) => ({ ...p, ...patch }));
      setIsSaving(true);
      try {
        const updated = await cardNotificationsService.update(organizationId, patch);
        setPrefs(updated);
        colocaToast.success(t("update_success"));
      } catch (error) {
        setPrefs(previous);
        colocaToast.error(HttpApiService.getErrorMessage(error));
      } finally {
        setIsSaving(false);
      }
    },
    [organizationId, isSaving, prefs, t],
  );

  const handleToggle = (event: CardNotificationEvent) => (next: boolean) => {
    if (!next) {
      void applyPatch({ [event]: false });
      return;
    }
    // Balance-low without a threshold has nothing to compare against, so it cannot be switched on.
    if (event === "balance_low" && parsePositive(thresholdInput) === null) {
      setThresholdError(tCards("notifications_balance_low_threshold_required_error"));
      return;
    }
    setPendingToggle(event);
  };

  const confirmPendingToggle = async () => {
    if (!pendingToggle) return;
    const event = pendingToggle;
    setPendingToggle(null);
    if (event === "balance_low") {
      await applyPatch({ balance_low: true, balance_low_threshold_usd: parsePositive(thresholdInput) });
      return;
    }
    await applyPatch({ [event]: true });
  };

  const handleThresholdChange = (raw: string) => {
    setThresholdInput(raw);
    setThresholdError(null);
  };

  const handleThresholdBlur = async () => {
    if (!organizationId || isSaving) return;
    const raw = thresholdInput.trim();
    if (raw === "") {
      // Empty with the toggle on is invalid: either enter a number or switch the alert off. Never
      // auto-clear a stored threshold while the alert is off — that is a value the user chose.
      if (prefs.balance_low) {
        setThresholdError(tCards("notifications_balance_low_threshold_required_error"));
      } else if (prefs.balance_low_threshold_usd !== null) {
        await applyPatch({ balance_low_threshold_usd: null });
      }
      return;
    }
    const parsed = parsePositive(raw);
    if (parsed === null) {
      setThresholdError(tCards("notifications_balance_low_threshold_required_error"));
      return;
    }
    setThresholdError(null);
    if (parsed !== prefs.balance_low_threshold_usd) await applyPatch({ balance_low_threshold_usd: parsed });
  };

  /** Sends one sample of `event`; resolves `false` when it did not go out, after saying why. */
  const sendTest = (event: CardNotificationEvent) => async (phone: string) => {
    if (!organizationId) return false;
    try {
      const result = await cardNotificationsService.sendTest(organizationId, event, phone);
      if (result.status === "skipped") {
        colocaToast.warning(t("send_test_skipped"));
        return false;
      }
      colocaToast.success(t("send_test_success"));
      return true;
    } catch (error) {
      colocaToast.error(HttpApiService.getErrorMessage(error));
      return false;
    }
  };

  const activeCount = CARD_NOTIFICATION_EVENTS.filter((event) => prefs[event]).length;

  return {
    organizationId,
    prefs,
    isLoading,
    isSaving,
    hasNoAccess,
    canWrite,
    /**
     * Locks every switch while a read or a write is in flight, without an organization, or without write
     * access.
     */
    controlsDisabled: isLoading || isSaving || !organizationId || !canWrite,
    activeCount,
    totalTypes: CARD_NOTIFICATION_EVENTS.length,
    pendingToggle,
    setPendingToggle,
    confirmPendingToggle,
    handleToggle,
    thresholdInput,
    thresholdError,
    handleThresholdChange,
    handleThresholdBlur,
    sendTest,
  };
}
